import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "../../utils/cn";

import { textVariants } from "./text";

const badgeVariants = cva(
    // Optical centering: asymmetric vertical padding (`pt-0.5` = 2px /
    // `pb-1` = 4px) shifts the glyphs ~1px up to counter the natural
    // ascender-heavy bias of most fonts. Typography (family/weight/
    // size/tracking/leading) flows through the `fine-print` Text variant.
    cn(
        textVariants({ variant: "fine-print" }),
        "group/badge inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-none border border-transparent px-2.5 pt-0.5 pb-1 whitespace-nowrap transition-colors has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&>svg]:pointer-events-none [&>svg]:size-3!"
    ),
    {
        variants: {
            variant: {
                // ── Status / state variants ─────────────────────
                // The "what is this entity currently doing?" axis.
                // `default` is the high-contrast emphasis pill;
                // `disabled` is the muted greyed-out version (was
                // `secondary` previously — renamed to free that
                // name for the graph-palette tag below). `error`
                // was `destructive` previously — renamed so the
                // status set reads as a four-tone block
                // (error / success / warning / disabled) instead
                // of mixing semantics.
                default: "bg-surface-inverse-raised text-foreground-inverse",
                disabled: "bg-surface-sunken text-foreground-base",
                error: "bg-error-inverse text-foreground-base",
                success: "bg-success-inverse text-foreground-base",
                warning: "bg-warning-inverse text-foreground-base",
                outline: "border-line-base text-foreground-base bg-transparent",
                // ── Category / tag variants ─────────────────────
                // Same primitive, different axis: instead of
                // "what state is this in?" these tag *what kind*
                // of thing the badge is labeling. Colors come from
                // the v3 graph palette (Figma graph/primary →
                // graph/denary) so a row of category badges reads
                // as the same visual language as the protocol
                // charts. quinary + denary are dark enough to need
                // inverse text — the others all pass contrast on
                // foreground-base.
                primary: "bg-graph-primary text-foreground-base",
                secondary: "bg-graph-secondary text-foreground-base",
                tertiary: "bg-graph-tertiary text-foreground-base",
                quaternary: "bg-graph-quaternary text-foreground-base",
                quinary: "bg-graph-quinary text-foreground-inverse",
                senary: "bg-graph-senary text-foreground-base",
                septenary: "bg-graph-septenary text-foreground-base",
                octonary: "bg-graph-octonary text-foreground-base",
                nonary: "bg-graph-nonary text-foreground-base",
                denary: "bg-graph-denary text-foreground-inverse"
            }
        },
        defaultVariants: {
            variant: "default"
        }
    }
);

function Badge({
    className,
    variant = "default",
    render,
    ...props
}: useRender.ComponentProps<"span"> &
    VariantProps<typeof badgeVariants>): React.ReactElement {
    return useRender({
        defaultTagName: "span",
        props: mergeProps<"span">(
            {
                className: cn(badgeVariants({ variant }), className)
            },
            props
        ),
        render,
        state: {
            slot: "badge",
            variant
        }
    }) as React.ReactElement;
}

export { Badge, badgeVariants };
